import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { CreditsModal } from "@/components/ui/credits-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { IMAGE_EDIT_MODEL } from "@/config/models";
import { UploadZone } from "@/components/generate/upload-zone";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { Loader2, Wand2, Star } from "lucide-react";
import { trackViewContent } from "@/services/analytics";

const MODEL = IMAGE_EDIT_MODEL;
const DRAFT_KEY = "couchstack_draft_imgedit";

function saveDraft(imageUrl: string | null, prompt: string) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ imageUrl, prompt })); } catch {}
}
function loadDraft(): { imageUrl: string | null; prompt: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.imageUrl || parsed.prompt)) return parsed;
  } catch {}
  return null;
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

export function ImageEditPage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  useEffect(() => {
    trackViewContent("Image Edit", user?.id, { funnel_stage: "create" });
  }, []);
  const { credits } = useUserStore();
  const generateMutation = useGenerate();
  const draft = loadDraft();
  const [imageUrl, setImageUrl] = useState<string | null>(draft?.imageUrl ?? null);
  const [prompt, setPrompt] = useState(draft?.prompt ?? "");
  const [showAuth, setShowAuth] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  useEffect(() => {
    if (imageUrl || prompt) saveDraft(imageUrl, prompt);
  }, [imageUrl, prompt]);

  const canGenerate = !!imageUrl && prompt.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (!imageUrl || !prompt.trim()) return;
    if (credits < MODEL.creditCost) {
      setShowCreditsModal(true);
      return;
    }
    try {
      const result = await generateMutation.mutateAsync({
        model_id: MODEL.id,
        prompt: prompt.trim(),
        input_url: imageUrl,
      });
      clearDraft();
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) {
      console.error("Generation failed:", err);
    }
  }, [imageUrl, prompt, credits, generateMutation, navigate]);

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6 animate-fade-in-up">
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#2d3127] tracking-tight mb-2">
          Image Edit
        </h1>
        <p className="text-sm text-[#8a8570]">
          Upload an image and describe how you want it changed
        </p>
      </div>

      <div className="space-y-4 mb-12 animate-fade-in-up delay-100">
        <UploadZone imageUrl={imageUrl} onImageChange={setImageUrl} />

        <div>
          <label className="block text-xs font-semibold text-[#8a8570] uppercase tracking-wider mb-2">Edit Instructions</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Make the sky a dramatic sunset, add warm golden tones to the entire image..."
            rows={3}
            className="input-field resize-none"
          />
        </div>

        {isSignedIn && (
          <div className="flex items-center justify-between text-xs text-[#8a8570]">
            <span className="flex items-center gap-1.5">
              <Star className="size-3 text-amber-600" />
              {MODEL.creditCost} stars · Basic
            </span>
            <span className="tabular-nums font-medium">
              Balance: <span className={credits >= MODEL.creditCost ? "text-[#2d3127]" : "text-red-500"}>{credits.toLocaleString()}</span>
            </span>
          </div>
        )}

        <Show when="signed-out">
          <button onClick={() => setShowAuth(true)} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2">
            <Wand2 className="size-4" />Edit Image
          </button>
          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
        </Show>
        <Show when="signed-in">
          <button onClick={handleGenerate} disabled={!canGenerate || generateMutation.isPending} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2">
            {generateMutation.isPending ? <><Loader2 className="size-4 animate-spin" />Editing...</> : <><Wand2 className="size-4" />Edit Image</>}
          </button>
        </Show>

        {generateMutation.isError && (
          <NsfwOrError error={generateMutation.error} />
        )}
      </div>

      <CreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={MODEL.creditCost}
        credits={credits}
      />
    </div>
  );
}
